import { useQuery } from "graphql-hooks";
import { useEffect, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { Button, Col, Form, FormFeedback, FormGroup, Input, Label, Row } from 'reactstrap';
import { useAPIErrorContext } from '../../contexts/apiErrorContext';
import { ErrorBanner } from '../Shared/ErrorBanner';

import { useFetchWrapper } from "../../hooks/useFetchWrapper";
import { ErrorPage } from "../Shared/ErrorPage";
import { Hero } from "../Shared/Hero";
import { Loading } from '../Shared/Loading';
import Helper from '../../utilities/helper';
import { PermissionsGate } from "../../utilities/PermissionsGate";
import { ACCESS_VALUES, PageNames } from "../../utilities/constValues";
import { PermissionsRequired } from "../Shared/PermissionsRequired";
import { MetaTags } from "../Shared/MetaTags";
import { clientConfig } from "../../utilities/configService";
import { FormContainer } from "../Shared/Forms/FormContainer";
import { FormHeading } from "../Shared/Forms/FormHeading";

const NEWFLEET_CMS_QUERY = `query datoQuery {
    pageEstimatorNewFleet {
        fieldLinkedText
        fieldHeroTitle
        fieldBodyHeader
        fieldBodyContent2
        fieldBodyContent1
        fieldBodyContent3
    }
    pageData: pageEstimatorNewFleet {
        seo: _seoMetaTags {
            attributes
            content
            tag
        }
    }
}`;

const EDITFLEET_CMS_QUERY = `query datoQuery {
    pageEstimatorEditFleet {
        fieldHeroTitle
        fieldBodyHeader
        fieldBodyContent1  
    }
    pageData: pageEstimatorEditFleet {
        seo: _seoMetaTags {
            attributes
            content
            tag
        }
    }
}`;

interface IOrganizationType {
    organizationTypeID: number,
    organizationTypeName: string
}

export const CreateEditFleet = () => {
    const fetchWrapper = useFetchWrapper();
    const { addError, clearError, trackError } = useAPIErrorContext();
    const applicationName = clientConfig?.APPLICATION_NAME || '';
    const navigate = useNavigate();

    const [organizationTypeOptions, setOrganizationTypeOptions] = useState([]);
    const [allFieldVerified, setAllFieldVerified] = useState(false);
    const [organizationTypeSelect, setOrganizationTypeSelect] = useState("0");
    const [fleetName, setFleetName] = useState('');
    const [formErrors, setFormErrors] = useState({ organizationType: '', fleetName: '' });
    const [newFleetForm, setNewFleetForm] = useState({ organizationType: '', fleetName: '' });
    const [fleetID, setFleetID] = useState('');

    const { fleetIDParam } = useParams();
    if (!fleetID) {
        if (fleetIDParam) {
            setFleetID(fleetIDParam);
        }
    }

    var CMS_QUERY = NEWFLEET_CMS_QUERY;
    if (fleetID) {
        CMS_QUERY = EDITFLEET_CMS_QUERY;
    }

    const { loading, error, data } = useQuery(CMS_QUERY);

    const location = useLocation();
    useEffect(() => {
        Helper.setPagePath(location.pathname);
    }, [location]);

    useEffect(() => {
        populateOrganizationType();
        if (fleetID) {
            getFleetByID();
        }
    }, [fleetID]);

    const populateOrganizationType = () => {
        fetchWrapper.get('organization/gettypes')
            .then((data) => {
                setOrganizationTypeOptions(data.organizationTypes);
            });
    };

    const getFleetByID = () => {
        fetchWrapper.get(`fleet/GetByID/${fleetID}`)
            .then((data) => {
                if (data.fleet) {
                    setOrganizationTypeSelect(data.fleet.organizationTypeID);
                    setFleetName(data.fleet.fleetName);
                    setNewFleetForm({
                        ...newFleetForm,
                        fleetName: data.fleet.fleetName
                    });
                }
            });
    };



    const errorClass = (error: string | any[]) => {
        return (error.length === 0 ? '' : 'is-invalid');
    }

    // Change handlers pass in the values they just set, since state updates are not visible until the next render.
    const validateForm = (form = newFleetForm, organizationType = organizationTypeSelect) => {
        var fieldValidationError = '';
        var fieldErrors = {
            organizationType: '',
            fleetName: ''
        }

        if (organizationType === '0') {
            fieldValidationError = "Organization Type is required.";
            fieldErrors.organizationType = "Organization Type is required.";
        }

        if (form.fleetName.trim().length <= 0) {
            fieldValidationError = 'Please name your fleet.';
            fieldErrors.fleetName = 'Please name your fleet.';
        }

        if (fieldValidationError.length > 0) {
            setFormErrors({
                organizationType: fieldErrors.organizationType,
                fleetName: fieldErrors.fleetName,
            });
            console.log("formErrors state: ", formErrors);
            return false;
        }
        else {
            return true;
        }
    }

    const validateAndSaveNewFleet = async (e: any) => {
        clearError();
        e.preventDefault();

        let formIsValid = validateForm();

        if (formIsValid) {
            var insertFleetRequest = {
                organizationTypeID: parseInt(organizationTypeSelect),
                fleetName: newFleetForm.fleetName,
                applicationName: applicationName
            };

            setFormErrors({
                organizationType: '',
                fleetName: '',
            });

            fetchWrapper.post('fleet', 'application/json', insertFleetRequest)
                .then((data) => {
                    if (data.success) {
                        onFleetSaved(data.fleetID);
                    }
                    else {
                        addError("Error submitting form", data.message);
                    }
                }).catch((error: any) => {
                    addError("Error submitting form", error);
                });
        }
    };

    const validateAndSaveEditedFleet = async (e: any) => {
        e.preventDefault();
        clearError();
        let formIsValid = validateForm();

        if (formIsValid) {
            var updateFleetRequest = {
                fleetID: fleetID,
                organizationTypeID: parseInt(organizationTypeSelect),
                fleetName: newFleetForm.fleetName,
                applicationName: applicationName
            };

            setFormErrors({
                organizationType: '',
                fleetName: '',
            });

            fetchWrapper.post('fleet/update', 'application/json', updateFleetRequest)
                .then((data) => {
                    if (data.success) {
                        onFleetEditOrCancel();
                    }
                    else {
                        addError("Error submitting form", data.message);
                    }
                }).catch((error: any) => {
                    addError("Error submitting form", error);
                });
        }
    };

    const handleOrganizationTypeChange = (e: { target: { value: any; }; }) => {
        setOrganizationTypeSelect(e.target.value);
        if (formErrors.organizationType !== '') {
            validateForm(newFleetForm, e.target.value);
        }
    };

    function handleFieldChanges() {
        if (newFleetForm.fleetName.trim().length > 0 && organizationTypeSelect !== '0') {
            setFormErrors({
                organizationType: "",
                fleetName: "",
            });
            setAllFieldVerified(true);
        }
        else {
            setAllFieldVerified(false);
        }
    }

    // Re-check whenever either field changes, whichever order they are filled in.
    useEffect(() => {
        handleFieldChanges();
    }, [newFleetForm.fleetName, organizationTypeSelect]);

    function handleFleetNameChange(e: { target: { value: any; }; }) {
        const updatedForm = {
            ...newFleetForm,
            fleetName: e.target.value
        };
        setNewFleetForm(updatedForm);
        if (formErrors.fleetName !== '') {
            validateForm(updatedForm);
        }
    }

    const onFleetSaved = (savedFleetID: string) => {
        navigate(`/AddVehicleSets/${savedFleetID}`)
    };

    const onFleetEditOrCancel = () => {
        clearError();
        navigate('/ViewFleets');
    }

    const showInstantEstimates = () => {
        navigate("/SampleFleets");
    }

    if (!error && !loading) {
        let datoContentData = data.pageEstimatorNewFleet ? data.pageEstimatorNewFleet : data.pageEstimatorEditFleet;
        return (
            <PermissionsGate requestedPermission={ACCESS_VALUES.Resources} renderError={<PermissionsRequired showParticipantMsg={true} />} pageName={PageNames.FleetEstimator}>
                <div className="createFleetContainer">
                    <Hero pageTitle={datoContentData.fieldHeroTitle} />
                    <ErrorBanner key="createEditFleet" errorCode="form" />
                    <Row>
                            <FormContainer title={datoContentData.fieldBodyHeader} description={datoContentData.fieldBodyContent1}>
                                <Row>
                                    <Col className="formBody">
                                        <FormHeading title={fleetName === '' ? "New Fleet" : fleetName} textAlign="alignCenter" light={false} />
                                        <div className="fleetRequestForm formInnerBody">
                                            <Row>
                                                <Col className="subheading">
                                                    A fleet is a model of your current fossil-fuel fleet.
                                                </Col>
                                            </Row>
                                            <hr />
                                            <Row>
                                                <Col sm={12}  >
                                                    <div className="text-align-center fieldsReq">All fields required</div>
                                                </Col>
                                            </Row>
                                            <Form>
                                                <FormGroup row className={`form-group ${errorClass(formErrors.organizationType)}`}>
                                                    <Label for="organizationType" sm={5} className="formRequired">Organization Type</Label>
                                                    <Col sm={7}>
                                                        <Input className="fieldWidth215" type="select" name="select" id="organizationType" value={organizationTypeSelect} required onChange={handleOrganizationTypeChange} invalid={formErrors.organizationType !== ''}>
                                                            <option key="0" value="0" disabled>
                                                                select...
                                                            </option>
                                                            {organizationTypeOptions.map((option: IOrganizationType) => {
                                                                return (
                                                                    <option key={option.organizationTypeID} onChange={handleFieldChanges} value={option.organizationTypeID}>
                                                                        {option.organizationTypeName}
                                                                    </option>)
                                                            })}
                                                        </Input>
                                                        <FormFeedback>{formErrors.organizationType}</FormFeedback>
                                                    </Col>
                                                </FormGroup>
                                                <FormGroup row className={`form-group ${errorClass(formErrors.fleetName)}`}>
                                                    <Label for="FleetName" sm={5} className="formRequired ">Fleet Name</Label>
                                                    <Col sm={7}>
                                                        <Input className="fieldWidth215" type="text" name="name" id="FleetName" placeholder="My Fleet" value={newFleetForm.fleetName} onChange={handleFleetNameChange} onKeyUp={handleFieldChanges} maxLength={128} invalid={formErrors.fleetName !== ''} />
                                                        <FormFeedback>{formErrors.fleetName}</FormFeedback>
                                                    </Col>
                                                </FormGroup>
                                                <hr />
                                                {!fleetID ? (
                                                    <div className="buttonRow">
                                                        <Button className="submitButton solidNeg secondaryButton iconRightArrow" onClick={validateAndSaveNewFleet} disabled={!allFieldVerified}>CONTINUE</Button>
                                                    </div>
                                                ) : (
                                                            <Row className="buttonRow twoButtons">
                                                                <Col lg={{ size: 5, order: 2 }} sm={{ size: 12, order: 1 }} className="rightButton">
                                                                    <Button className="submitButton solidNeg secondaryButton iconRightArrow" onClick={validateAndSaveEditedFleet}>SAVE</Button>
                                                                </Col>
                                                                <Col lg={{ size: 5, order: 1 }} sm={{ size: 12, order: 2 }} className="leftButton">
                                                            <Button className="cancelButton hollowPos secondaryButton iconReturnArrowBlack" onClick={onFleetEditOrCancel}>CANCEL</Button>
                                                        </Col>
                                                                
                                                    </Row>
                                                )}
                                            </Form>
                                        </div>
                                    </Col>
                                </Row>
                                <Row>
                                    <Col className="formFooter">
                                        {datoContentData.fieldBodyContent2 ? (
                                            <div>
                                                {datoContentData.fieldBodyContent2.toString().startsWith('<p>') ? (
                                                    <div className="contentBlock formContainer ctaMessage" onClick={showInstantEstimates} dangerouslySetInnerHTML={{ __html: datoContentData.fieldBodyContent2.replace("[fieldLinkedText]", "<span>" + datoContentData.fieldLinkedText + "</span>") }} />
                                                ) : (
                                                    <div className="contentBlock formContainer ctaMessage" onClick={showInstantEstimates} dangerouslySetInnerHTML={{ __html: "<p>" + datoContentData.fieldBodyContent2.replace("[fieldLinkedText]", "<span>" + datoContentData.fieldLinkedText + "</span>") + "</p>" }} />
                                                )
                                                }
                                            </div>
                                        ) : (
                                            <div className="noCTA" ></div>
                                        )}
                                        {datoContentData.fieldBodyContent3 ? (
                                            <div className="contentBlock formContainer disclaimer" dangerouslySetInnerHTML={{ __html: datoContentData.fieldBodyContent3 }} ></div>
                                        ) : ('')}
                                    </Col>
                                </Row>
                            </FormContainer>
                    </Row>

                    {/* set meta tags */}
                    <MetaTags seoData={data.pageData.seo} />
                </div>
            </PermissionsGate>
        );

    }
    else if (!error) {
        return (<Loading showContent={true} includeHero={true} />);
    }
    else {
        trackError("Dato error", JSON.stringify(error));
        return (<ErrorPage showContactForm={true} includeHero={true} />);
    }
};
